/**
 * La lista de comprobantes del historial de ventas.
 *
 * Desde `md` es una `<Table>` (06 §3.1: hay una columna de plata que se compara
 * entre filas): Fecha · Numero · Cliente · Estado · Total · acciones, con el
 * chip de moneda en CADA fila porque la columna mezcla pesos y dolares.
 * Bajo `md` sigue siendo la lista de tarjetas-registro.
 *
 * LA FILA ES DE UNA SOLA LINEA, Y ESO SE PAGO EN ALGUN LADO
 * ---------------------------------------------------------
 * Antes cada fila traia ademas la cotizacion congelada debajo de la fecha y,
 * en las ventas en dolares, el equivalente en pesos como segunda linea del
 * importe. Dos alturas distintas de fila y la columna de plata imposible de
 * escanear de corrido.
 *
 * Todo eso no se borro: bajo al panel que se abre al hacer click en la fila
 * (`renderDetalle`), que es donde ademas se ve como se cobro. El importe de la
 * fila va SOLO en la moneda en que se facturo, que es la unica que el
 * comprobante tiene de verdad.
 *
 * Esta separada de la ruta para poder montarla en un test sin router ni
 * queries: recibe las filas ya filtradas, los totales ya calculados y avisa que
 * accion se pidio.
 */
import { createContext, useContext, useState, type ReactNode } from "react";
import { formatearCotizacion } from "@tora/shared";
import { ChevronRight, FileText, MoreHorizontal, Undo2 } from "lucide-react";

import type { Comprobante, TotalesVentas as TotalesDelPeriodo } from "@/lib/tipos";

import { Importe } from "@/components/money/importe";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { IconButton } from "@/components/ui/icon-button";
import { SkeletonTabla, SkeletonTarjetas } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { AccionesRemito } from "@/components/venta/AccionesRemito";
import { aNumero } from "@/lib/tipos";
import { cn, formatearFecha } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Estado del comprobante
// ─────────────────────────────────────────────────────────────────────────────

export type EstadoVenta =
  "anulado" | "impago" | "parcial" | "pagada" | "contado" | "con_nc" | "parte_con_nc";

/** MASTER §10: pagada success · parcial warning · impaga destructive · anulada secondary. */
export const TONO_ESTADO: Record<EstadoVenta, NonNullable<BadgeProps["variant"]>> = {
  anulado: "secondary",
  impago: "destructive",
  parcial: "warning",
  pagada: "success",
  contado: "success",
  // No es plata que entro: la venta se cancelo con una nota de credito.
  con_nc: "secondary",
  parte_con_nc: "secondary",
};

export const ETIQUETA_ESTADO: Record<EstadoVenta, string> = {
  anulado: "anulado",
  impago: "impago",
  parcial: "pago parcial",
  pagada: "pagada",
  contado: "contado",
  con_nc: "cancelada con NC",
  parte_con_nc: "parte con NC",
};

/**
 * `GET /ventas` trae ademas `pagado` y `condicion_venta`, que el tipo compartido
 * todavia no declara. Se leen con un tipo local en vez de `as any`.
 */
type ComprobanteListado = Comprobante & {
  pagado?: string | number | null;
  condicion_venta?: string | null;
  /** Lo cancelado con una nota de credito, y con cual (0121_2). */
  devuelto?: string | number | null;
  notas_credito?: string | null;
};

/** Los estados que se muestran de un comprobante, en el orden en que van. */
export function estadosDe(c: Comprobante): EstadoVenta[] {
  const fila = c as ComprobanteListado;
  const anulado = c.estado === "ANULADO";
  if (anulado) return ["anulado"];

  const estados: EstadoVenta[] = [];
  const pagado = aNumero(fila.pagado ?? 0);
  const total = aNumero(c.total);
  // Todo lo cancelado vino de una nota de credito: no se pago, se devolvio (o
  // la NC se imputo a esta venta). Decia «PAGADA» por algo que el cliente
  // devolvio.
  const devuelto = aNumero(fila.devuelto ?? 0);
  if (
    c.tipo !== "NC" &&
    total - pagado <= 0.009 &&
    devuelto > 0.009 &&
    pagado - devuelto <= 0.009
  ) {
    return ["con_nc"];
  }
  // Una NC no se cobra: nunca esta impaga.
  if (c.tipo !== "NC" && total - pagado > 0.009) {
    // Parcial o impaga por la PLATA que entro, no por lo cancelado: una venta
    // a cuenta de $ 28.000 con una NC de $ 13.000 figuraba «pago parcial»
    // como si el cliente hubiera entregado plata, y no pago nada.
    estados.push(pagado - devuelto > 0.009 ? "parcial" : "impago");
    if (devuelto > 0.009) estados.push("parte_con_nc");
  } else if (c.tipo !== "NC" && fila.condicion_venta === "CUENTA_CORRIENTE" && total > 0.009) {
    // Una venta a cuenta que ya se cobro entera quedaba sin ningun estado: ni
    // impaga ni contado, una fila muda. Se dice que esta pagada.
    estados.push("pagada");
  }
  // «Contado» solo si quedo saldado. Una venta de contado con pago parcial
  // deja deuda en la cuenta corriente, y mostraba «PAGO PARCIAL | CONTADO» a la
  // vez: dos estados que se contradicen.
  if (fila.condicion_venta === "CONTADO" && estados.length === 0) estados.push("contado");
  return estados;
}

// ─────────────────────────────────────────────────────────────────────────────
// Totales
// ─────────────────────────────────────────────────────────────────────────────

export interface TotalesVentas {
  /** Facturado en pesos, neto de notas de credito. */
  ars: number;
  /** Facturado en dolares, neto de notas de credito. */
  usd: number;
  /** Todo llevado a pesos a la cotizacion congelada de CADA comprobante. */
  enArs: number;
  cantidadArs: number;
  cantidadUsd: number;
  anulados: number;
}

/**
 * Los totales van separados por moneda de FACTURACION y nunca sumados entre
 * si: un comprobante en dolares y uno en pesos no se suman sin decir a que
 * cotizacion, y cada uno tiene la suya congelada. `enArs` es el unico que
 * mezcla, y por eso lleva la cotizacion al lado donde se muestra (MASTER §3.4).
 */
export function totalizarVentas(filas: ReadonlyArray<Comprobante>): TotalesVentas {
  const t: TotalesVentas = {
    ars: 0,
    usd: 0,
    enArs: 0,
    cantidadArs: 0,
    cantidadUsd: 0,
    anulados: 0,
  };
  for (const c of filas) {
    if (c.estado === "ANULADO") {
      t.anulados += 1;
      continue;
    }
    const signo = c.tipo === "NC" ? -1 : 1;
    if (c.moneda === "USD") {
      t.usd += signo * aNumero(c.total);
      t.cantidadUsd += 1;
    } else {
      t.ars += signo * aNumero(c.total);
      t.cantidadArs += 1;
    }
    t.enArs += signo * aNumero(c.total_ars);
  }
  return t;
}

/**
 * "1 comprobante" / "8 comprobantes".
 *
 * La cantidad al lado de cada total es lo que deja ver que las dos lineas del
 * pie son grupos disjuntos y no la misma plata mirada dos veces: 8 + 6 = 14.
 */
function rotuloComprobantes(cantidad: number): string {
  return cantidad === 1 ? "comprobante" : "comprobantes";
}

// ─────────────────────────────────────────────────────────────────────────────
// La lista
// ─────────────────────────────────────────────────────────────────────────────

// Fecha, Numero, Cliente, Estado, Total, acciones y la del chevron.
const COLUMNAS = 7;

export interface ListaVentasProps {
  filas: ReadonlyArray<Comprobante>;
  /** Anular es de admin: sin esto el menu de la fila no aparece. */
  esAdmin: boolean;
  /** Nombre por `cliente_id`: la lista de `/ventas` trae solo el id. */
  nombresClientes?: ReadonlyMap<number, string>;
  /**
   * Los totales del SET FILTRADO COMPLETO, calculados por el backend.
   *
   * No se suman aca a proposito. `filas` es una pagina: sumarla daria el total
   * de lo que se trajo, que con un mes grande no es el total del mes y no
   * habria forma de darse cuenta mirando la pantalla. Sin esto, el pie no se
   * dibuja: es preferible no mostrar un total a mostrar uno parcial.
   */
  totales?: TotalesDelPeriodo;
  /** Primera carga, sin datos previos: se muestra el skeleton. */
  cargando?: boolean;
  /** Refetch con datos en pantalla: la lista se atenua, no se vacia. */
  actualizando?: boolean;
  mensajeVacio?: string;
  /** Con filtros activos, el vacio ofrece limpiarlos. */
  onLimpiarFiltros?: () => void;
  /**
   * El `PieDeLista` de quien usa la lista: cuantas se trajeron de cuantas hay.
   *
   * Va dentro del cuerpo con scroll y no debajo de la Card porque la pantalla
   * es de alto fijo: un bloque mas abajo le comeria el alto a la tabla. Y es el
   * lugar correcto igual, porque se lee justo cuando se llego al final.
   */
  pie?: ReactNode;
  onNotaCredito: (id: number) => void;
  onAnular: (id: number) => void;
  /**
   * El panel que se abre debajo de la fila. Lo trae quien usa la lista y no lo
   * arma ella: adentro hay una consulta por comprobante, y esta lista se monta
   * en los tests sin router ni `QueryClient`.
   */
  renderDetalle?: (comprobante: Comprobante) => ReactNode;
}

export function ListaVentas({
  filas,
  esAdmin,
  nombresClientes,
  totales,
  cargando = false,
  actualizando = false,
  mensajeVacio = "Todavía no hay comprobantes.",
  onLimpiarFiltros,
  pie,
  onNotaCredito,
  onAnular,
  renderDetalle,
}: ListaVentasProps): JSX.Element {
  const vacio = filas.length === 0;
  const primeraCarga = cargando && vacio;
  // Mobile queda como estaba (fuera del alcance de este rediseño) y sigue
  // sumando la pagina; la tabla usa `totales`, que es del periodo entero.
  const totalesTarjeta = totalizarVentas(filas);
  // Una sola fila abierta por vez: dos paneles abiertos en una lista con scroll
  // propio dejan de ser "el detalle de esto" y pasan a ser dos bloques sueltos.
  const [abierta, setAbierta] = useState<number | null>(null);
  const limpiar = onLimpiarFiltros ? (
    <Button type="button" variant="outline" size="sm" onClick={onLimpiarFiltros}>
      Limpiar filtros
    </Button>
  ) : undefined;

  // Sin filtros y sin nada: es el primer dia, no un filtro que no encontro.
  if (vacio && !cargando && !onLimpiarFiltros) {
    return <EstadoVacio icono={FileText} titulo={mensajeVacio} />;
  }

  const accionesDe = (c: Comprobante): JSX.Element => (
    <AccionesVenta
      comprobante={c}
      esAdmin={esAdmin}
      onNotaCredito={onNotaCredito}
      onAnular={onAnular}
    />
  );

  return (
    <div
      aria-busy={actualizando || undefined}
      className={cn("transition-opacity duration-150", actualizando && "opacity-60")}
    >
      {/*
        Desktop: la tabla se lleva el alto que sobra (`alto="lleno"`) en vez de
        ponerse un techo propio, y por eso la Card necesita `h-full`: sin eso la
        cadena de alturas se corta aca y vuelve el doble scroll.
      */}
      <Card padding="none" className="hidden h-full overflow-hidden md:block">
        {primeraCarga ? (
          <SkeletonTabla columnas={COLUMNAS} />
        ) : (
          <Table densidad="normal" alto="lleno">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <span className="sr-only">Abrir</span>
                </TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Número</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Acciones</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vacio ? (
                <TableVacia colSpan={COLUMNAS} mensaje={mensajeVacio} accion={limpiar} />
              ) : (
                filas.map((c) => (
                  <FilaVenta
                    key={c.id}
                    comprobante={c}
                    cliente={nombreCliente(c, nombresClientes)}
                    acciones={accionesDe(c)}
                    abierta={abierta === c.id}
                    onAlternar={
                      renderDetalle
                        ? () => setAbierta((actual) => (actual === c.id ? null : c.id))
                        : undefined
                    }
                    detalle={abierta === c.id ? renderDetalle?.(c) : undefined}
                  />
                ))
              )}
              {!vacio && pie ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMNAS}>{pie}</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
            {/*
              Una linea por moneda de facturacion y NINGUNA que las sume: un
              comprobante en dolares y uno en pesos no se suman sin decir a que
              cotizacion, y cada uno tiene la suya congelada (MASTER §3.4). La
              cantidad de comprobantes va al lado porque es lo que deja ver que
              son dos grupos disjuntos y no dos vistas de la misma plata.

              Dice "En pesos" y no "Ventas en pesos" porque la lista no siempre
              son ventas: con la solapa en NC o ND esto totaliza notas.
            */}
            {!vacio && totales ? (
              <TableFooter>
                {totales.ars.cantidad > 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      En pesos
                      <span className="pl-2 font-normal tabular-nums text-muted-foreground">
                        {totales.ars.cantidad} {rotuloComprobantes(totales.ars.cantidad)}
                      </span>
                    </TableCell>
                    <TableCellImporte monto={aNumero(totales.ars.importe)} moneda="ARS" />
                    <TableCell />
                  </TableRow>
                ) : null}
                {totales.usd.cantidad > 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      En dólares
                      <span className="pl-2 font-normal tabular-nums text-muted-foreground">
                        {totales.usd.cantidad} {rotuloComprobantes(totales.usd.cantidad)}
                      </span>
                    </TableCell>
                    <TableCellImporte monto={aNumero(totales.usd.importe)} moneda="USD" />
                    <TableCell />
                  </TableRow>
                ) : null}
              </TableFooter>
            ) : null}
          </Table>
        )}
      </Card>

      {/* Mobile: una tarjeta-registro por comprobante y los totales al final. */}
      <div className="flex flex-col gap-2 md:hidden">
        {primeraCarga ? (
          <SkeletonTarjetas />
        ) : vacio ? (
          <EstadoVacio icono={FileText} titulo={mensajeVacio} accion={limpiar} />
        ) : (
          <>
            {filas.map((c) => (
              <TarjetaVenta
                key={c.id}
                comprobante={c}
                cliente={nombreCliente(c, nombresClientes)}
                acciones={accionesDe(c)}
              />
            ))}
            <TotalesTarjeta totales={totalesTarjeta} />
            {pie}
          </>
        )}
      </div>
    </div>
  );
}

/** Sin cliente es venta de mostrador; con id pero sin nombre cargado, el numero. */
function nombreCliente(
  c: Comprobante,
  nombres: ReadonlyMap<number, string> | undefined,
): string | null {
  if (c.cliente_id === null) return null;
  return nombres?.get(c.cliente_id) ?? `Cliente #${c.cliente_id}`;
}

interface FilaProps {
  comprobante: Comprobante;
  cliente: string | null;
  acciones: JSX.Element;
}

interface FilaVentaProps extends FilaProps {
  abierta?: boolean;
  /** Sin esto la fila no se abre: la lista puede usarse sin panel. */
  onAlternar?: () => void;
  detalle?: ReactNode;
}

function FilaVenta({
  comprobante: c,
  cliente,
  acciones,
  abierta = false,
  onAlternar,
  detalle,
}: FilaVentaProps): JSX.Element {
  const anulado = c.estado === "ANULADO";
  const esNc = c.tipo === "NC";
  const total = aNumero(c.total);
  const idPanel = `detalle-venta-${c.id}`;

  return (
    <>
      <TableRow
        className={cn("group", onAlternar && "cursor-pointer", abierta && "bg-muted")}
        onClick={
          onAlternar
            ? (e) => {
                // Las acciones viven adentro de la fila pero no la abren: sin
                // esto, mandar el remito por WhatsApp abriria el panel de paso.
                // Se marca el subarbol y se ignora, en vez de ponerle un
                // onClick a un <span>, que no es un elemento interactivo.
                if ((e.target as HTMLElement).closest("[data-sin-abrir]")) return;
                onAlternar();
              }
            : undefined
        }
      >
        <TableCell className="w-8 pr-0">
          {onAlternar ? (
            /*
              Un boton de verdad y no un icono decorativo: es el unico camino al
              panel para el teclado y el lector de pantalla, que necesitan el
              `aria-expanded` y el `aria-controls`. El click de la fila lo
              duplica solo para el mouse.
            */
            <IconButton
              icono={ChevronRight}
              tamano="sm"
              aria-expanded={abierta}
              aria-controls={abierta ? idPanel : undefined}
              aria-label={`${abierta ? "Cerrar" : "Abrir"} el detalle de ${c.numero}`}
              className={cn(
                "shrink-0 text-muted-foreground [&_svg]:transition-transform [&_svg]:duration-150",
                abierta && "[&_svg]:rotate-90",
              )}
              onClick={(e) => {
                // Si no, el click sube a la fila y la vuelve a alternar: abre y
                // cierra en el mismo gesto.
                e.stopPropagation();
                onAlternar();
              }}
            />
          ) : null}
        </TableCell>
        <TableCell className="whitespace-nowrap text-sm tabular-nums">
          {formatearFecha(c.fecha)}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          <Codigo
            className={cn(
              "text-sm font-medium",
              anulado ? "text-muted-foreground line-through" : "text-foreground",
            )}
          >
            {c.numero}
          </Codigo>
        </TableCell>
        <TableCell className="max-w-[14rem] truncate text-sm">
          {cliente ?? <span className="text-muted-foreground">Consumidor final</span>}
        </TableCell>
        <TableCell>
          <EstadosDeVenta comprobante={c} />
        </TableCell>
        {/*
          Chip en CADA fila: la columna mezcla pesos y dolares (MASTER §3.4).
          Una sola linea y una sola moneda —la de facturacion—: el equivalente
          en pesos de una venta en dolares vive en el panel, donde se puede
          decir a que cotizacion. Una NC va en negativo con el signo de Intl, no
          con un "−" pegado a mano.
        */}
        <TableCellImporte>
          <Importe
            monto={esNc ? -total : total}
            moneda={c.moneda}
            className={cn(anulado && "line-through opacity-60")}
          />
        </TableCellImporte>
        {/*
          Las acciones aparecen con el mouse encima o cuando algo adentro tiene
          el foco (`focus-within`), que es lo que las deja alcanzables con Tab:
          un `group-hover` solo seria una fila sin acciones para el teclado.
          Ocupan su lugar igual (`invisible`, no `hidden`) para que el ancho de
          la columna no cambie al pasar por arriba.
        */}
        <TableCell className="whitespace-nowrap text-right">
          <span
            data-sin-abrir
            className="invisible inline-flex items-center justify-end gap-1 group-focus-within:visible group-hover:visible"
          >
            {acciones}
          </span>
        </TableCell>
      </TableRow>

      {abierta && detalle ? (
        <TableRow className="hover:bg-transparent">
          <TableCell id={idPanel} colSpan={COLUMNAS} className="bg-muted p-0">
            {detalle}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

/**
 * Tarjeta-registro (06 §3.4): numero y estado a la izquierda, el importe
 * arriba a la derecha con su chip, y las acciones en la misma linea.
 */
function TarjetaVenta({ comprobante: c, cliente, acciones }: FilaProps): JSX.Element {
  const anulado = c.estado === "ANULADO";
  const esNc = c.tipo === "NC";
  const total = aNumero(c.total);

  // Número e importe arriba, fecha y cliente abajo a todo el ancho, y las
  // acciones en su propio renglón. En una sola fila con el ícono, el importe
  // y los cinco botones, a la fecha y al cliente les quedaban de 9 a 22 px y
  // salían una palabra por renglón.
  return (
    <Card as="article" padding="compacto" className="flex items-start gap-3">
      <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Codigo
              className={cn(
                "text-sm font-medium",
                anulado ? "text-muted-foreground line-through" : "text-foreground",
              )}
            >
              {c.numero}
            </Codigo>
            <EstadosDeVenta comprobante={c} />
          </div>

          <div className="flex shrink-0 flex-col items-end">
            <Importe
              monto={esNc ? -total : total}
              moneda={c.moneda}
              className={cn(anulado && "line-through opacity-60")}
            />
            {c.moneda === "USD" ? (
              <Importe
                monto={esNc ? -aNumero(c.total_ars) : aNumero(c.total_ars)}
                moneda="ARS"
                tamano="sm"
                className={cn(anulado && "line-through opacity-60")}
              />
            ) : null}
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          {new Date(c.fecha).toLocaleDateString("es-AR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
          {cliente ? ` · ${cliente}` : null}
          {/* La cotizacion congelada de ESE comprobante, no la de hoy: es la
              que decide cuanto valia esa venta. */}
          {" · dólar a "}
          {formatearCotizacion(aNumero(c.cotizacion))}
        </div>
        <div className="flex justify-end">{acciones}</div>
      </div>
    </Card>
  );
}

/**
 * Las ventas con algo vendido a pedido que todavía no llegó, o que llegó y el
 * cliente no se llevó (0111). Lo carga la pantalla una vez; cada fila lo lee.
 */
export type EncargoDeVenta = "POR_RECIBIR" | "POR_ENTREGAR";
const EncargosDeVentas = createContext<ReadonlyMap<number, EncargoDeVenta>>(new Map());
export const ProveedorEncargosDeVentas = EncargosDeVentas.Provider;

function EstadosDeVenta({ comprobante }: { comprobante: Comprobante }): JSX.Element {
  const encargo = useContext(EncargosDeVentas).get(comprobante.id);
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {estadosDe(comprobante).map((e) => (
        <Badge key={e} variant={TONO_ESTADO[e]}>
          {ETIQUETA_ESTADO[e]}
        </Badge>
      ))}
      {encargo && comprobante.estado !== "ANULADO" ? (
        <Badge variant={encargo === "POR_RECIBIR" ? "warning" : "info"}>
          {encargo === "POR_RECIBIR" ? "por recibir" : "por entregar"}
        </Badge>
      ) : null}
    </span>
  );
}

/** Los mismos totales del pie de la tabla, para la lista de tarjetas. */
function TotalesTarjeta({ totales }: { totales: TotalesVentas }): JSX.Element | null {
  if (totales.cantidadArs === 0 && totales.cantidadUsd === 0) return null;
  return (
    <Card as="section" variant="muted" padding="compacto" className="flex flex-col gap-1 text-sm">
      {totales.cantidadArs > 0 ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Total en pesos</span>
          <Importe monto={totales.ars} moneda="ARS" />
        </div>
      ) : null}
      {totales.cantidadUsd > 0 ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Total en dólares</span>
          <Importe monto={totales.usd} moneda="USD" />
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Las acciones de un comprobante, iguales en la fila y en la tarjeta.
 *
 * Reenviar el remito (el cliente lo pide al dia siguiente), acreditar con nota
 * de credito (de cualquiera que atiende: deja rastro en vez de borrar; solo
 * sobre ventas, una NC no se acredita, se anula) y Anular, que es de admin y
 * solo sirve para un comprobante sin cobros. Anular va en el menu y no como
 * icono suelto: es irreversible y al lado de la NC se tocaba por accidente.
 */
function AccionesVenta({
  comprobante: c,
  esAdmin,
  onNotaCredito,
  onAnular,
}: {
  comprobante: Comprobante;
  esAdmin: boolean;
  onNotaCredito: (id: number) => void;
  onAnular: (id: number) => void;
}): JSX.Element | null {
  const anulado = c.estado === "ANULADO";
  if (anulado) return null;

  return (
    <>
      <AccionesRemito numero={c.numero} token={c.token_publico} />

      {c.tipo === "VENTA" ? (
        <IconButton
          icono={Undo2}
          tamano="sm"
          aria-label={`Nota de crédito de ${c.numero}`}
          title="Nota de crédito"
          className="shrink-0"
          onClick={() => onNotaCredito(c.id)}
        />
      ) : null}

      {esAdmin ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton
              icono={MoreHorizontal}
              tamano="sm"
              aria-label="Más acciones"
              className="shrink-0"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              aria-label={`Anular ${c.numero}`}
              onSelect={() => onAnular(c.id)}
            >
              Anular
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </>
  );
}
